import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  date,
  datetime,
  timestamp,
  boolean,
  mysqlEnum,
  index,
  unique,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";

/**
 * Asset module (reskin). Ported by reconciling each table's create + every up() ALTER across
 * the 16 Modules/Asset migrations + 8 core database/migrations asset migrations (down() ignored).
 *
 * Reconciliation highlights:
 * - company_id (int unsigned, nullable, after id, FK companies cascade/cascade) added by
 *   2022_09_02 to asset_types/assets/asset_lending_history.
 * - assets: +image (2020_02_22), +value/location/added_by/last_updated_by (2021_08_11),
 *   status enum extended to 6 values via raw ALTER (2023_05_19), +asset_tag(unique)/
 *   warranty_expires_on(index)/vendor/condition/department (2026_06_06).
 * - asset_settings: +notify_update(2023_11_02) +license_type(2023_11_28) +purchased_on(2023_12_19).
 *   2020_03_08 (asset_lending_history.lender_id/returner_id) wrapped FK_CHECKS=0 but is a real up() ALTER — ported.
 * - Core 2026 reskin tables: asset_types.canonical_category enum; asset_mobile_meta; asset_bundles;
 *   asset_audit_log; asset_requests; asset_dispositions (+ status/requested_by/requested_at workflow ALTER).
 *
 * Note: users.id is int unsigned (increments); assets.id is bigint unsigned (bigIncrements) — FK column types match.
 * Deferred FKs (typed, NO .references()): none cross-batch — all targets are companies/users/own-file tables.
 */

/** asset_types — create (2020_01_12) + company_id (2022_09_02) + canonical_category (2026_06_05). */
export const assetTypes = mysqlTable(
  "asset_types",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    name: varchar("name", { length: 255 }).notNull(),
    canonicalCategory: mysqlEnum("canonical_category", [
      "laptop",
      "monitor",
      "cpu",
      "mobile_ios",
      "mobile_android",
      "keyboard",
      "mouse",
      "adapter",
      "cable",
      "headset",
      "tablet",
      "other",
    ]).default("other"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [index("asset_types_canonical_category_index").on(t.canonicalCategory)],
);

/**
 * assets — create (2020_01_12) + company_id (2022_09_02) + image (2020_02_22)
 * + value/location/added_by/last_updated_by (2021_08_11) + status 6-value ALTER (2023_05_19)
 * + asset_tag/warranty_expires_on/vendor/condition/department (2026_06_06).
 */
export const assets = mysqlTable(
  "assets",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    assetTag: varchar("asset_tag", { length: 30 }),
    companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
      onDelete: "cascade",
      onUpdate: "cascade",
    }),
    name: varchar("name", { length: 100 }).notNull(),
    assetTypeId: bigint("asset_type_id", { mode: "number", unsigned: true }).references(
      () => assetTypes.id,
      { onDelete: "set null", onUpdate: "cascade" },
    ),
    serialNumber: varchar("serial_number", { length: 255 }),
    image: varchar("image", { length: 255 }),
    description: text("description"),
    status: mysqlEnum("status", [
      "lent",
      "available",
      "non-functional",
      "lost",
      "damaged",
      "under-maintenance",
    ])
      .default("available")
      .notNull(),
    value: varchar("value", { length: 255 }),
    warrantyExpiresOn: date("warranty_expires_on"),
    vendor: varchar("vendor", { length: 191 }),
    condition: mysqlEnum("condition", ["excellent", "good", "fair", "poor", "broken"]),
    department: varchar("department", { length: 100 }),
    location: varchar("location", { length: 255 }),
    addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
      onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("serial_number").on(t.serialNumber),
    unique("assets_asset_tag_unique").on(t.assetTag),
    index("assets_warranty_expires_on_index").on(t.warrantyExpiresOn),
  ],
);

/**
 * asset_lending_history — create (2020_01_12) + company_id (2022_09_02)
 * + lender_id (notNull) / returner_id (nullable) (2020_03_08).
 */
export const assetLendingHistory = mysqlTable("asset_lending_history", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  assetId: bigint("asset_id", { mode: "number", unsigned: true })
    .notNull()
    .references(() => assets.id, { onDelete: "cascade", onUpdate: "cascade" }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  lenderId: int("lender_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  returnerId: int("returner_id", { unsigned: true }).references(() => users.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  dateGiven: datetime("date_given").notNull(),
  returnDate: datetime("return_date"),
  dateOfReturn: datetime("date_of_return"),
  notes: text("notes"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * asset_settings — create (2020_02_21) + notify_update (2023_11_02) + license_type (2023_11_28)
 * + purchased_on (2023_12_19). NO company_id (legacy never added it to this table).
 */
export const assetSettings = mysqlTable("asset_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  purchaseCode: varchar("purchase_code", { length: 255 }),
  supportedUntil: timestamp("supported_until"),
  notifyUpdate: boolean("notify_update").default(true),
  licenseType: varchar("license_type", { length: 20 }),
  purchasedOn: timestamp("purchased_on"),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/** asset_mobile_meta — 1:1 with assets (2026_06_05). */
export const assetMobileMeta = mysqlTable(
  "asset_mobile_meta",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    assetId: bigint("asset_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => assets.id, { onDelete: "cascade", onUpdate: "cascade" }),
    deviceModel: varchar("device_model", { length: 191 }),
    osVersion: varchar("os_version", { length: 50 }),
    imei: varchar("imei", { length: 50 }),
    purchaseDate: date("purchase_date"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    unique("asset_mobile_meta_asset_id_unique").on(t.assetId),
    index("asset_mobile_meta_imei_index").on(t.imei),
  ],
);

/** asset_bundles — self-link on assets (primary/member) (2026_06_05). */
export const assetBundles = mysqlTable(
  "asset_bundles",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    primaryAssetId: bigint("primary_asset_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => assets.id, { onDelete: "cascade", onUpdate: "cascade" }),
    memberAssetId: bigint("member_asset_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => assets.id, { onDelete: "cascade", onUpdate: "cascade" }),
    bundledAt: datetime("bundled_at").notNull(),
    unbundledAt: datetime("unbundled_at"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("asset_bundles_primary_asset_id_unbundled_at_index").on(t.primaryAssetId, t.unbundledAt),
    index("asset_bundles_member_asset_id_index").on(t.memberAssetId),
  ],
);

/** asset_audit_log — one row per change event (2026_06_06). created_at useCurrent, no updated_at. */
export const assetAuditLog = mysqlTable(
  "asset_audit_log",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    assetId: bigint("asset_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => assets.id, { onDelete: "cascade" }),
    userId: int("user_id", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
    }),
    event: varchar("event", { length: 30 }).notNull(),
    changes: text("changes"),
    summary: varchar("summary", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (t) => [
    index("asset_audit_log_asset_id_created_at_index").on(t.assetId, t.createdAt),
    index("asset_audit_log_event_index").on(t.event),
  ],
);

/**
 * asset_requests — employee supply/device requests (2026_06_15). Self-service flow.
 * status string default 'pending'. FK note from migration: user_id is int unsigned, asset_id is bigint unsigned.
 */
export const assetRequests = mysqlTable(
  "asset_requests",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    userId: int("user_id", { unsigned: true })
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemLabel: varchar("item_label", { length: 120 }).notNull(),
    itemKey: varchar("item_key", { length: 40 }),
    reason: text("reason").notNull(),
    notes: text("notes"),
    replacesAssetId: bigint("replaces_asset_id", { mode: "number", unsigned: true }).references(
      () => assets.id,
      { onDelete: "set null" },
    ),
    assignedAssetId: bigint("assigned_asset_id", { mode: "number", unsigned: true }).references(
      () => assets.id,
      { onDelete: "set null" },
    ),
    oldItemDisposition: varchar("old_item_disposition", { length: 20 }),
    status: varchar("status", { length: 16 }).default("pending").notNull(),
    decisionNote: text("decision_note"),
    approvedBy: int("approved_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
    }),
    decidedAt: timestamp("decided_at"),
    fulfilledAt: timestamp("fulfilled_at"),
    acknowledgedAt: timestamp("acknowledged_at"),
    createdBy: int("created_by", { unsigned: true }),
    lastUpdatedBy: int("last_updated_by", { unsigned: true }),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("asset_requests_company_status").on(t.companyId, t.status),
    index("asset_requests_company_user").on(t.companyId, t.userId),
  ],
);

/**
 * asset_dispositions — permanent disposed register (2026_06_15) + request workflow ALTER
 * (2026_06_15_180000: status default 'disposed', requested_by, requested_at).
 */
export const assetDispositions = mysqlTable(
  "asset_dispositions",
  {
    id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
    companyId: int("company_id", { unsigned: true })
      .notNull()
      .references(() => companies.id, { onDelete: "cascade" }),
    assetId: bigint("asset_id", { mode: "number", unsigned: true })
      .notNull()
      .references(() => assets.id, { onDelete: "cascade" }),
    disposition: varchar("disposition", { length: 16 }).notNull(),
    status: varchar("status", { length: 12 }).default("disposed"),
    reason: text("reason"),
    requestId: bigint("request_id", { mode: "number", unsigned: true }).references(
      () => assetRequests.id,
      { onDelete: "set null" },
    ),
    prevStatus: varchar("prev_status", { length: 24 }),
    disposedBy: int("disposed_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
    }),
    disposedAt: timestamp("disposed_at"),
    requestedBy: int("requested_by", { unsigned: true }),
    requestedAt: timestamp("requested_at"),
    reinstatedAt: timestamp("reinstated_at"),
    reinstatedBy: int("reinstated_by", { unsigned: true }).references(() => users.id, {
      onDelete: "set null",
    }),
    reinstateNote: text("reinstate_note"),
    createdAt: timestamp("created_at"),
    updatedAt: timestamp("updated_at"),
  },
  (t) => [
    index("asset_dispositions_company_id_reinstated_at_index").on(t.companyId, t.reinstatedAt),
    index("asset_dispositions_asset_id_index").on(t.assetId),
  ],
);
